/**
 * Catalog Admin page (BON-161). Tabbed shell — Items / Attributes / Requests —
 * over the /api/v1/inventory/catalog API. See docs/specs/CATALOG_ADMIN_UI.md.
 *
 * UI edit affordances gate on WRITE_INVENTORY; the backend enforces the real
 * `write:all` boundary. Reconciling the two (a dedicated catalog-admin
 * permission) is a follow-up — see the spec's open questions.
 */
import { useState } from 'react';

import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';

import AttributeDefinitionsTab from './AttributeDefinitionsTab';
import ItemsTab from './ItemsTab';
import RequestsTab from './RequestsTab';

type Tab = 'items' | 'attributes' | 'requests';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'items', label: 'Items' },
  { id: 'attributes', label: 'Attributes' },
  { id: 'requests', label: 'Requests' },
];

export default function CatalogPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.WRITE_INVENTORY);
  const [tab, setTab] = useState<Tab>('items');

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex gap-1 border-b border-stone-200 dark:border-stone-700">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition ${
              tab === t.id
                ? 'border-moss-600 text-bark-900 dark:text-stone-100 font-semibold'
                : 'border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'items' && <ItemsTab canEdit={canEdit} />}
      {tab === 'attributes' && <AttributeDefinitionsTab canEdit={canEdit} />}
      {tab === 'requests' && <RequestsTab />}
    </div>
  );
}
